// src/pages/settings/medicationCatalog/MedicationCatalogTab.tsx
//
// S5 (ClickUp 86d42twq8) — converted from the standalone `/settings/medications-catalog`
// route (`MedicationCatalogPage.tsx`) into the settings drawer's CLINICAL group. `Layout` and
// `PageContainer` are stripped; the rail names the tab, so the page title and subtitle go
// with them. Everything else — the peptide-only toolbar suppression included — is unchanged.
import React, { useState, useEffect, useCallback } from "react";
import { Button, Tag, Space, Switch, Modal, Tooltip, Typography } from "antd";
import { PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined, StopOutlined, CheckCircleOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { getSessionUser } from "../../../utils/auth/sessionUser";
import { DataTable, StatusTag } from "../../../components/shared";
import PageToolbar from "../../../components/shared/PageToolbar";
import MedicationCatalogDrawer from "./components/MedicationCatalogDrawer";
import { MedicationCatalogItem, MEDICATION_CATEGORIES, defaultDosageGuide } from "../../../types/healthcare/MedicationCatalogTypes";
import { fetchCatalogMedications, deleteCatalogMedication, toggleCatalogMedicationActive } from "../../../api/healthcare/MedicationCatalogApi";
import { useNotifications } from "../../../context/NotificationContext";
import { hasFeature } from "../../../utils/subscriptions/subscriptionService";

const { Text } = Typography;

const MedicationCatalogTab: React.FC = () => {
	const user = getSessionUser();
	const company: string = user.company;
	const isPeptideOnly = hasFeature("healthcare.peptide") && !hasFeature("healthcare");

	const [items, setItems] = useState<MedicationCatalogItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");
	const [categoryFilter, setCategoryFilter] = useState("");
	const [showInactive, setShowInactive] = useState(false);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editItem, setEditItem] = useState<Partial<MedicationCatalogItem> | null>(null);
	const { showError, showSuccess } = useNotifications();

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const data = await fetchCatalogMedications({
				company,
				search: search || undefined,
				category: categoryFilter || undefined,
				include_inactive: showInactive || undefined,
			});
			setItems(data);
		} catch {
			showError("Failed to load medication catalog");
		} finally {
			setLoading(false);
		}
	}, [company, search, categoryFilter, showInactive]);

	useEffect(() => {
		load();
	}, [load]);

	const openNew = () => {
		setEditItem(null);
		setDrawerOpen(true);
	};
	const openEdit = (item: MedicationCatalogItem) => {
		setEditItem(item);
		setDrawerOpen(true);
	};

	const handleToggle = async (item: MedicationCatalogItem) => {
		try {
			const updated = await toggleCatalogMedicationActive(item.id);
			setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
			showSuccess(`Medication ${updated.is_active ? "activated" : "deactivated"}`);
		} catch {
			showError("Failed to update status");
		}
	};

	const handleDelete = (item: MedicationCatalogItem) => {
		Modal.confirm({
			title: `Delete "${item.name}"?`,
			content: "This removes the medication from the catalog. Existing patient prescriptions are unaffected.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deleteCatalogMedication(item.id);
					setItems((prev) => prev.filter((i) => i.id !== item.id));
					showSuccess("Medication deleted");
				} catch {
					showError("Failed to delete medication");
				}
			},
		});
	};

	const handleSaved = (saved: MedicationCatalogItem) => {
		setItems((prev) => {
			const idx = prev.findIndex((i) => i.id === saved.id);
			return idx >= 0 ? prev.map((i) => (i.id === saved.id ? saved : i)) : [saved, ...prev];
		});
		setDrawerOpen(false);
	};

	const handleReset = () => {
		setSearch("");
		setCategoryFilter("");
		setShowInactive(false);
	};

	const columns: ColumnsType<MedicationCatalogItem> = [
		{
			title: "Name",
			key: "name",
			sorter: (a, b) => a.name.localeCompare(b.name),
			render: (_, r) => (
				<div>
					<Text strong>{r.name}</Text>
					{r.admin_medication_id && (
						<Tag style={{ fontSize: 10, borderColor: 'var(--mg-info)', background: 'var(--mg-info-bg)', color: 'var(--mg-info)', marginLeft: 6 }}>
							Global
						</Tag>
					)}
					{r.generic_name && (
						<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
							{r.generic_name}
						</Text>
					)}
					{r.brand_names && r.brand_names.length > 0 && (
						<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
							{r.brand_names.slice(0, 2).join(", ")}
							{r.brand_names.length > 2 ? ` +${r.brand_names.length - 2}` : ""}
						</Text>
					)}
				</div>
			),
		},
		{
			title: "Category",
			dataIndex: "medication_category",
			key: "category",
			width: 140,
			render: (v) => (v ? <Tag style={{ borderColor: "var(--mg-border)", background: "var(--mg-bg-subtle)", color: "var(--mg-text)" }}>{v}</Tag> : <Text type="secondary">—</Text>),
		},
		{
			title: "Schedule",
			dataIndex: "schedule",
			key: "schedule",
			width: 90,
			render: (v) => (v ? <Tag style={{ borderColor: "var(--mg-primary-border)", background: "var(--mg-primary-bg)", color: "var(--mg-primary)" }}>{v}</Tag> : <Text type="secondary">—</Text>),
		},
		{
			title: "Dosage",
			key: "dosage",
			width: 160,
			render: (_, r) => {
				const guides = r.dosage_guides ?? [];
				const guide = defaultDosageGuide(guides);
				if (!guide) return <Text type="secondary">—</Text>;
				return (
					<div>
						<Space size={4} wrap>
							<Text style={{ fontSize: 12 }}>{guide.dosage || "—"}</Text>
							{guides.length > 1 && (
								<Tag style={{ fontSize: 10, borderColor: "var(--mg-border)", background: "var(--mg-bg-subtle)", color: "var(--mg-text-secondary)" }}>
									+{guides.length - 1} more
								</Tag>
							)}
						</Space>
						{guide.label && (
							<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
								{guide.label}
							</Text>
						)}
					</div>
				);
			},
		},
		{
			title: "Flags",
			key: "flags",
			width: 130,
			render: (_, r) => (
				<Space size={4} wrap>
					{r.high_risk && (
						<Tag icon={<WarningOutlined />} style={{ fontSize: 10, borderColor: "var(--mg-danger)", background: "var(--mg-danger-bg)", color: "var(--mg-danger)" }}>
							High Risk
						</Tag>
					)}
					{r.controlled_substance && (
						<Tag style={{ fontSize: 10, borderColor: "var(--mg-warning)", background: "var(--mg-warning-bg)", color: "var(--mg-warning)" }}>
							Controlled
						</Tag>
					)}
					{r.requires_script && (
						<Tag style={{ fontSize: 10, borderColor: "var(--mg-info)", background: "var(--mg-info-bg)", color: "var(--mg-info)" }}>
							Script
						</Tag>
					)}
				</Space>
			),
		},
		{
			title: "Status",
			key: "status",
			width: 110,
			render: (_, r) => <StatusTag status={r.is_active ? "active" : "inactive"} />,
		},
		{
			title: "",
			key: "actions",
			width: 110,
			render: (_, r) => (
				<Space size={2}>
					<Tooltip title={r.admin_medication_id ? 'Managed by the global admin system' : 'Edit'}>
						<span onClick={(e) => { if (r.admin_medication_id) e.stopPropagation(); }}>
							<Button type="text" size="small" icon={<EditOutlined />} disabled={!!r.admin_medication_id} onClick={(e) => { e.stopPropagation(); if (!r.admin_medication_id) openEdit(r); }} />
						</span>
					</Tooltip>
					<Tooltip title={r.admin_medication_id ? 'Managed by the global admin system' : (r.is_active ? 'Deactivate' : 'Activate')}>
						<span onClick={(e) => { if (r.admin_medication_id) e.stopPropagation(); }}>
							<Button type="text" size="small" icon={r.is_active ? <StopOutlined /> : <CheckCircleOutlined />} disabled={!!r.admin_medication_id} onClick={(e) => { e.stopPropagation(); if (!r.admin_medication_id) handleToggle(r); }} />
						</span>
					</Tooltip>
					<Tooltip title={r.admin_medication_id ? 'Managed by the global admin system' : 'Delete'}>
						<span onClick={(e) => { if (r.admin_medication_id) e.stopPropagation(); }}>
							<Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={!!r.admin_medication_id} onClick={(e) => { e.stopPropagation(); if (!r.admin_medication_id) handleDelete(r); }} />
						</span>
					</Tooltip>
				</Space>
			),
		},
	];

	return (
		<>
			{!isPeptideOnly && (
				<PageToolbar
					primaryAction={{ label: "New Medication", icon: <PlusOutlined />, onClick: openNew }}
					filters={[
						{
							type: "select",
							placeholder: "Category",
							options: MEDICATION_CATEGORIES,
							value: categoryFilter,
							onChange: setCategoryFilter,
							width: 160,
						},
						{
							type: "search",
							placeholder: "Search name, NDC, PBS…",
							value: search,
							onChange: setSearch,
							width: 220,
						},
						{
							type: "custom",
							render: (
								<Space size={6}>
									<Switch size="small" checked={showInactive} onChange={setShowInactive} />
									<Text style={{ fontSize: 13, color: "var(--mg-text-secondary)" }}>Show inactive</Text>
								</Space>
							),
						},
					]}
					onReset={handleReset}
				/>
			)}

			<DataTable
				columns={columns}
				dataSource={items}
				rowKey="id"
				loading={loading}
				emptyTitle="No medications found"
				emptyDescription="Add your first medication to the catalog to get started"
				onRowClick={openEdit}
				pagination={{ pageSize: 25 }}
				scrollX={900}
			/>

			<MedicationCatalogDrawer open={drawerOpen} item={editItem} onClose={() => setDrawerOpen(false)} onSaved={handleSaved} company={company} />
		</>
	);
};

export default MedicationCatalogTab;
